/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  DatePicker,
  message,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';

import {
  getPercepcionesIncidenciasReloj,
  postPercepcionIncidenciaReloj,
  patchPercepcionIncidenciaReloj,
  deletePercepcionIncidenciaReloj,
  getPercepcionIncidenciaReloj,
} from 'api/catalogos/percepcionesIncidenciasReloj';
import { ESTATUS_ACTIVO } from 'utils/constants';
import moment from 'moment';
import { fr } from 'date-fns/locale';
import { getEvaluacionesAsistencia } from 'api/catalogos/TiposDeEvaluacionAsistencia';

const PercepcionIncidenciasReloj = ({ percepcionId, permission, disabled }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);

  const [tiposEvaluacion, setTiposEvaluacion] = useState([]);

  const [selectedData, setSelectedData] = useState({});
  const [disableFields, setDisableFields] = useState(false);

  const formInstances = [form];

  const fetchData = async () => {
    if (!percepcionId) {
      setData({ results: [] });
      return;
    }
    setLoading(true);
    try {
      const _data = await getPercepcionesIncidenciasReloj({ percepcion: percepcionId });
      setData(formatReceived(_data));
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [_estados, _tiposEvaluacion] = await Promise.all([
          getEstadosGlobales(),
          // eslint-disable-next-line max-len
          getEvaluacionesAsistencia ? getEvaluacionesAsistencia().catch(() => []) : Promise.resolve([]),
        ]);

        if (mounted) {
          setEstados(_estados);
          setTiposEvaluacion(_tiposEvaluacion);
          if (percepcionId) {
            await fetchData();
          }
        }
      } catch (err) {
        onError(err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchAll();

    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [percepcionId]);

  const onCancel = () => {
    setVisible(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    try {
      const [key] = selectedRowKeys;
      const match = responseData || data.results.find((item) => item.id === key);
      if (!match) return;

      const _record = await getPercepcionIncidenciaReloj(match.id);

      if (_record) {
        form.resetFields();
        const formatted = formatReceived(_record);

        // eslint-disable-next-line max-len
        if (formatted.vigencia_inicial) formatted.vigencia_inicial = moment(formatted.vigencia_inicial);
        if (formatted.vigencia_final) formatted.vigencia_final = moment(formatted.vigencia_final);

        // Si el tipo de evaluación viene como objeto, extraemos ID
        if (typeof formatted.tipo_de_evaluacion === 'object') {
          formatted.tipo_de_evaluacion = formatted.tipo_de_evaluacion?.id;
        }

        form.setFieldsValue(formatted);
        setDisableFields(_record?.estados_globales !== ESTATUS_ACTIVO);
        setVisible(true);
      }
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      values.percepcion = percepcionId;

      if (values.vigencia_inicial) values.vigencia_inicial = values.vigencia_inicial.format('YYYY-MM-DD');
      if (values.vigencia_final) values.vigencia_final = values.vigencia_final.format('YYYY-MM-DD');

      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchPercepcionIncidenciaReloj(key, values) : postPercepcionIncidenciaReloj(values));

      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePercepcionIncidenciaReloj(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    if (!percepcionId) {
      message.warning('Debes guardar la Percepción antes de agregar incidencias.');
      return;
    }
    form.resetFields();
    onCancel();
    form.setFieldsValue({
      estados_globales: ESTATUS_ACTIVO,
    });
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Tipo de Evaluación',
      dataIndex: 'tipo_de_evaluacion',
      key: 'tipo_de_evaluacion',
      width: 250,
      render: (val) => {
        if (typeof val === 'object') return val.nombre || val.descripcion;
        const found = tiposEvaluacion.find((t) => t.id === val);
        return found ? found.nombre || found.descripcion : val;
      },
    },
    {
      titleText: 'Tolerancia',
      dataIndex: 'tolerancia',
      key: 'tolerancia',
      width: 150,
    },
    {
      titleText: 'Vigencia Inicial',
      dataIndex: 'vigencia_inicial',
      key: 'vigencia_inicial',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = { required: true, message: 'Campo requerido' };

  return (
    <Row style={{ marginTop: 20 }}>
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          titleTable="Incidencias Reloj Checador"
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          disabled={disabled || !percepcionId}
          allowExportPDF={false}
        />

        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          width={800}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Incidencia`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="formIncidencia"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Row gutter={16}>
              {/* Tipo de Evaluación */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="tipo_de_evaluacion"
                  label="Tipo de Evaluación"
                  rules={[requiredRule]}
                  hasFeedback
                >
                  <Select
                    dataSource={tiposEvaluacion}
                    keyProp="id"
                    labelProp="nombre"
                    disabled={disableFields}
                  />
                </Form.Item>
              </Col>

              {/* Tolerancia */}
              <Col xs={24} md={12}>
                <Form.Item
                  name="tolerancia"
                  label="Tolerancia"
                  rules={[requiredRule]}
                  hasFeedback
                  help="Minutos de tolerancia (ej: 10)"
                >
                  <Input type="number" disabled={disableFields} />
                </Form.Item>
              </Col>

              {/* Vigencias */}
              <Col xs={24} md={12}>
                <Form.Item name="vigencia_inicial" label="Vigencia Inicial" rules={[requiredRule]} hasFeedback>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="vigencia_final" label="Vigencia Final" hasFeedback>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled={disableFields} />
                </Form.Item>
              </Col>

              {/* Estatus */}
              <Col xs={24} md={12}>
                <Form.Item name="estados_globales" label="Estatus" rules={[requiredRule]} hasFeedback>
                  <Select
                    dataSource={estados}
                    disabled={selectedRowKeys.length <= 0 || disableFields}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden><Button htmlType="submit" /></Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content="¿Eliminar este registro?"
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default PercepcionIncidenciasReloj;
